<template>
  <div class="navigation">
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav container">
      <!-- Navigation items left -->
      <ul class="navigation__list">
        <li
          v-for="(tier1, index) of left.items"
          :key="tier1.handle"
          class="navigation__item"
        >
          <template v-if="tier1.links.length || getContentBlocks(index + 1)">
            <a
              :id="`${tier1.handle}-menu`"
              class="navigation__link js-mega-menu-link text-heading-m"
              :class="{ [$class('active')]: currentMegaMenuHandle === tier1.handle }"
              :href="tier1.url"
              :aria-expanded="currentMegaMenuHandle === tier1.handle"
              aria-haspopup="true"
              @mouseover="handleMegaMenuToggle(tier1.handle)"
              @focus="handleMegaMenuToggle(tier1.handle)"
              v-text="tier1.title"
            />

            <mega-menu
              :ref="`${tier1.handle}-menu`"
              :blocks="getContentBlocks(index + 1)"
              :aria-labelledby="`${tier1.handle}-menu`"
              :handle="tier1.handle"
              :current-handle="currentMegaMenuHandle"
              :menu-links="tier1"
            />
          </template>

          <a
            v-else
            :id="`${tier1.handle}-menu`"
            class="navigation__link text-heading-m"
            :href="tier1.url"
            @mouseover="handleMegaMenuToggle('close')"
            @focus="handleMegaMenuToggle('close')"
            v-text="tier1.title"
          />
        </li>
      </ul>
      <!-- Logo -->
      <a
        class="navigation__logo"
        :href="$variable('routes.root')"
        @mouseover="handleMegaMenuToggle('close')"
        @focus="handleMegaMenuToggle('close')"
      >
        <span
          class="visually-hidden"
          v-text="$variable('store.shopName')"
        />

        <icon-logo />
      </a>
      <!-- Navigation items right -->
      <ul class="navigation__list">
        <li
          v-for="(tier1, index) of right.items"
          :key="tier1.handle"
          class="navigation__item"
        >
          <template
            v-if="
              tier1.links.length ||
                getContentBlocks(index + 1 + left.items.length)
            "
          >
            <a
              :id="`${tier1.handle}-menu`"
              class="navigation__link js-mega-menu-link text-heading-m"
              :class="{ [$class('active')]: currentMegaMenuHandle === tier1.handle }"
              :href="tier1.url"
              :aria-expanded="currentMegaMenuHandle === tier1.handle"
              aria-haspopup="true"
              @mouseover="handleMegaMenuToggle(tier1.handle)"
              @focus="handleMegaMenuToggle(tier1.handle)"
              v-text="tier1.title"
            />

            <mega-menu
              :ref="`${tier1.handle}-menu`"
              :blocks="getContentBlocks(index + 1 + left.items.length)"
              :aria-labelledby="`${tier1.handle}-menu`"
              :handle="tier1.handle"
              :current-handle="currentMegaMenuHandle"
              :menu-links="tier1"
            />
          </template>

          <a
            v-else
            :id="`${tier1.handle}-menu`"
            class="navigation__link text-heading-m"
            :href="tier1.url"
            @mouseover="handleMegaMenuToggle('close')"
            @focus="handleMegaMenuToggle('close')"
            v-text="tier1.title"
          />
        </li>
      </ul>
    </nav>
  </div>

  <!-- Mega menu overlay. (no content) -->
  <teleport
    to="body"
  >
    <overlay
      key="megaMenu"
      block-class="mega-menu-overlay"
      namespace="megaMenu"
      no-content
      type="modal"
      @force-close="closeMegaMenu"
    />
  </teleport>
</template>

<script>

/**
 * Vue: Navigation (navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} left - Desktop left linklist handle and items.
 * @param {Object} right - Desktop right linklist handle and items.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import MegaMenu from '~async/mega-menu/mega-menu'
import Overlay from '~global/overlay/overlay'
import IconLogo from '~icons/general/logo.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    MegaMenu,
    Overlay,
    IconLogo,
  },

  props: {
    blocks: {
      type: [Boolean, Object],
      default: () => false,
    },
    left: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    right: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
  },

  data() {
    return {
      currentMegaMenuHandle: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      overlaysQueue: (state) => state.overlays.active.length,
    }),
  },

  watch: {

    /**
     * Watch overlay queue to force close stuck mega menu
     */
    overlaysQueue(count) {
      if (count > 1) {
        this.closeMegaMenu()
      }
    },
  },

  mounted() {

    /**
     * Hanlde ESC keypress to force close stuck mega menu
     */
    cnvs.EventBus.listen('cnvs:esc', () => {
      if (this.getActiveOverlay.namespace === 'megaMenu') {
        this.closeMegaMenu()
      }
    })

    this.handleMouseOut()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',

      toggleOverlay: 'overlays/toggle',
      updateOverlay: 'overlays/update',
    }),

    /**
     * Get the middle index of the nav list
     */
    midIndex() {
      return Math.floor(this.desktop.items.length / 2)
    },

    /**
     * Get content blocks for mega menus
     * @param {Number} index - mega menu index to find the matching blocks
     */
    getContentBlocks(index) {
      if (!this.blocks) {
        return false
      }

      return this.blocks.find((block) => {
        return block.position === index
      })
    },

    /**
     * Handle mega menu toggle.
     * - stuck modal
     * @param {String} handle - selected mega menu handle
     */
    handleMegaMenuToggle(handle) {
      if (handle === 'close') {
        this.closeMegaMenu()
        return
      }

      this.currentMegaMenuHandle = handle

      if (!this.getActiveOverlay || this.getActiveOverlay.handle !== 'megaMenu') {
        this.toggleOverlay({
          component: 'mega-menu',
          ignoreDismissed: true,
          namespace: 'megaMenu',
          stuck: true,
        })

        disableBodyScroll(this.$refs[`${handle}-menu`], { reserveScrollBarGap: true })
      }
    },

    /**
     * Handle mouseout
     * - closes the mega menu when mouseout
     */
    handleMouseOut() {
      window.addEventListener('mouseover', (event) => {
        if (this.currentMegaMenuHandle) {
          const target = event.target
          const classList = target?.classList
          const isMegaMenu = classList?.contains('js-mega-menu') ||
            target.closest('.js-mega-menu')
          const isMegaMenuLink = classList?.contains('js-mega-menu-link') ||
            target.closest('.js-mega-menu-link')
          const siteHeader = classList?.contains('site-header') ||
            target.closest('.site-header')

          if (!isMegaMenu && !isMegaMenuLink && !siteHeader) {
            this.closeMegaMenu()
          }
        }
      })
    },

    /**
     * Handle mega menu close.
     * - unstuck modal
     */
    closeMegaMenu() {
      this.updateOverlay({
        namespace: 'megaMenu',
        update: { stuck: false },
      })

      this.closeOverlay('megaMenu')
      clearAllBodyScrollLocks()
      this.currentMegaMenuHandle = ''
    },
  },
}
</script>
